import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Dropdown } from "@mui/base/Dropdown";
import { MenuButton } from "@mui/base/MenuButton";
import { Menu } from "@mui/base/Menu";
import { MenuItem } from "@mui/base/MenuItem";
import {
  ChevronDown, LogOut, User, KeyRound, X,
  LayoutDashboard, Clock, Calendar, DollarSign,
  FileText, TrendingUp, Users2, Scale, Briefcase,
  Globe, Menu as MenuIcon, Shield, Target, Award
,  TicketCheck, Receipt} from "lucide-react";
import styles from "../admin/components/Admin/admin.module.scss";
import Images from "assets/Images";
import { apiBaseUrl } from '../../../services/config';
import { ThemeProvider, ThemeToggle, useTheme } from "context/ThemeContext";
const API = apiBaseUrl;

// ── Logo — inverts to black in light mode (same as AdminLayout) ────
const LogoImg = ({ theme }) => {
  const src = Images?.LOGO || Images?.logo || Images?.hirality || Images?.brandLogo;
  const imgStyle = {
    height: 150,
    objectFit: "contain",
    filter: theme === "light" ? "brightness(0) saturate(100%)" : "none",
    transition: "filter 0.25s",
  };
  return src ? (
    <>
      <img src={src} alt="Hirality" style={imgStyle}
        onError={e => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}/>
      <div style={{ display:"none", alignItems:"center", justifyContent:"center",
        fontSize:22, fontWeight:900,
        color: theme === "light" ? "#1A2332" : "#00C8FF" }}>
        Hirality
      </div>
    </>
  ) : (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:22, fontWeight:900,
      color: theme === "light" ? "#1A2332" : "#00C8FF" }}>
      Hirality
    </div>
  );
};

const getEmpNav = (isManager) => [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    to: "/employee/dashboard",
  },
  {
    id: "attendance",
    label: "Attendance & Leave",
    icon: Clock,
    paths: ["/employee/attendance", "/employee/leaves", "/employee/holidays"],
    children: [
      { label: "Attendance",       to: "/employee/attendance", icon: Clock    },
      { label: "My Leaves",        to: "/employee/leaves",     icon: Calendar },
      { label: "Holiday Calendar", to: "/employee/holidays",   icon: Globe    },
    ],
  },
    ...(isManager ? [{
    id: "team",
    label: "My Team",
    icon: Users2,
    paths: ["/employee/team","/employee/team/teamtracker", "/employee/team/leaves", "/employee/team/attendance", "/employee/team/appraisals", "/employee/team/goals", "/employee/team/compoff"],
    children: [
      { label: "Team Attendance", to: "/employee/team/teamtracker", icon: Clock },
      { label: "Leave Requests",        to: "/employee/team/leaves",      icon: Calendar    },
      { label: "Attendance Correction", to: "/employee/team/attendance",  icon: Clock       },
      { label: "Appraisals",            to: "/employee/team/appraisals",  icon: Award       },
      { label: "Goals & KPIs",          to: "/employee/team/goals",       icon: Target      },
      { label: "Comp-Off",          to: "/employee/team/compoff",       icon: Target      },

    ],
  }] : []),
  {
    id: "payroll",
    label: "Payroll & Tax",
    icon: DollarSign,
    paths: ["/employee/payslips", "/employee/investment"],
    children: [
      { label: "My Payslips",    to: "/employee/payslips",   icon: FileText },
      { label: "Investment / Tax", to: "/employee/investment", icon: Scale  },
    ],
  },
  {
  id: "reimbursements",
  label: "Reimbursements",
  icon: Receipt,
  paths: ["/employee/reimbursements"],
  children: [
    { label: "My Claims", to: "/employee/reimbursements",     icon: Receipt  },
    // { label: "New Claim", to: "/employee/reimbursements/new", icon: FileText },
  ],
},
  {
    id: "documents",
    label: "Documents",
    icon: Briefcase,
    paths: ["/employee/documents"],
    children: [
      { label: "Company Documents", to: "/employee/documents", icon: FileText },
    ],
  },
  {
    id: "performance",
    label: "Performance",
    icon: TrendingUp,
    paths: ["/employee/performance", "/employee/goals"],
    children: [
      { label: "My Appraisals",   to: "/employee/performance", icon: TrendingUp },
      { label: "My Goals & KPIs", to: "/employee/goals",       icon: Target     },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: TicketCheck,
    to: "/employee/support",
  },

];

function EmployeeLayoutInner() {
  const { T, theme } = useTheme();
  const navigate   = useNavigate();
  const location   = useLocation();
  const sidebarRef = useRef(null);

  // Only one top-level menu can be open at a time
  const [openMenu,    setOpenMenu]    = useState("attendance");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 960);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Read user from sessionStorage
  const user = (() => {
    try {
      const su = sessionStorage.getItem("platform_user");
      if (su) return JSON.parse(su);
      return JSON.parse(localStorage.getItem("platform_user") || "{}");
    } catch { return {}; }
  })();

  const isManager   = ["ceo","cto","vp","senior_manager","manager","team_lead","company_admin"].includes(user.role);
  const NAV         = getEmpNav(isManager);
  const displayName = user.full_name || `${user.first_name||""} ${user.last_name||""}`.trim() || user.email?.split("@")[0] || "Employee";
  const initials    = displayName.split(" ").filter(Boolean).map(w=>w[0]).join("").toUpperCase().slice(0,2) || "EM";
  const avatarColor = user.avatar_color || "#00C8FF";
  const roleLbl     = (user.role||"employee").replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());

  const [showResetPwd, setShowResetPwd] = useState(false);
  const [pwdForm,      setPwdForm]      = useState({ current:"", next:"", confirm:"" });
  const [pwdMsg,       setPwdMsg]       = useState("");

  useEffect(() => {
    const onResize = () => {
      setScreenWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth > 960);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) setSidebarOpen(false);
    };
    if (sidebarOpen && screenWidth < 961) document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [sidebarOpen, screenWidth]);

  useEffect(() => {
    // Keep current path's section open (already open by default, no change needed)
  }, [location.pathname]);

  const toggleSidebar  = () => setSidebarOpen(p => !p);
  const handleNavClick = () => { if (screenWidth < 961) setSidebarOpen(false); };

  const logout = () => {
    sessionStorage.clear();
    localStorage.removeItem("platform_token");
    localStorage.removeItem("platform_user");
    localStorage.removeItem("isLoggedIn");
    navigate("/login");
  };

  return (
    <div className={styles.layoutWrapper} style={{ background:T.bg, transition:"background 0.25s" }}>
      {sidebarOpen && screenWidth < 961 && (
        <div className={`${styles.sidebarOverlay} ${styles.show}`} onClick={() => setSidebarOpen(false)}/>
      )}

      <aside ref={sidebarRef} className={`${styles.sidebar} ${sidebarOpen ? styles.open : styles.closed}`}
        style={{ background:T.sidebar_bg, borderRight:`1px solid ${T.sidebar_border}`,
          transition:"background 0.25s, border-color 0.25s" }}>
        {/* ── Logo ── */}
        <div className={styles.logoContainer}>
          <LogoImg theme={theme}/>
        </div>

        {/* ── Nav — single-level items link directly, others accordion (one open at a time) ── */}
        <nav className={styles.navMenu}>
          <ul>
            {NAV.map((item) => {
              const Icon = item.icon;

              if (item.to) {
                return (
                  <li key={item.id}>
                    <NavLink to={item.to} onClick={handleNavClick} style={{ textDecoration:"none" }}>
                      {({ isActive }) => (
                        <div
                          style={{
                            display:"flex", alignItems:"center", gap:9,
                            padding:"10px 10px", cursor:"pointer", borderRadius:8,
                            color: isActive ? T.nav_text_active : T.nav_text,
                            background: isActive ? T.sidebar_active : "transparent",
                            transition:"background 0.15s, color 0.15s",
                          }}
                          onMouseEnter={e=>{ if(!isActive) e.currentTarget.style.background = T.sidebar_hover; }}
                          onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background = "transparent"; }}>
                          <span className={styles.menuIcon}>
                            <Icon size={16} style={{ color: isActive ? T.nav_text_active : T.nav_text }}/>
                          </span>
                          <p style={{ margin:0, flex:1, textAlign:"left", fontSize:12.5,
                            color: isActive ? T.nav_text_active : T.nav_text,
                            fontWeight: isActive ? 700 : 500 }}>{item.label}</p>
                        </div>
                      )}
                    </NavLink>
                  </li>
                );
              }

              const isOpen = openMenu === item.id;
              const toggle  = () => setOpenMenu(prev => prev === item.id ? null : item.id);
              return (
                <li key={item.id} className={isOpen ? styles.active : ""}>
                  <a onClick={toggle}
                    style={{
                      display:"flex", alignItems:"center", gap:9,
                      padding:"10px 10px", cursor:"pointer", borderRadius:8,
                      color: isOpen ? T.nav_text_active : T.nav_text,
                      background: isOpen ? T.sidebar_active : "transparent",
                      transition:"background 0.15s, color 0.15s",
                    }}
                    onMouseEnter={e=>{ if(!isOpen) e.currentTarget.style.background = T.sidebar_hover; }}
                    onMouseLeave={e=>{ if(!isOpen) e.currentTarget.style.background = "transparent"; }}>
                    <span className={styles.menuIcon}>
                      <Icon size={16} style={{ color: isOpen ? T.nav_text_active : T.nav_text }}/>
                    </span>
                    <p style={{ margin:0, flex:1, textAlign:"left", fontSize:12.5,
                      color: isOpen ? T.nav_text_active : T.nav_text,
                      fontWeight: isOpen ? 700 : 500 }}>{item.label}</p>
                    <ChevronDown size={14} className={styles.chevron}
                      style={{ color:T.nav_text, transform: isOpen ? "rotate(180deg)" : "none",
                        transition:"transform 0.25s, color 0.15s" }}/>
                  </a>
                  {isOpen && (
                    <ul className={styles.submenu} style={{ background:T.submenu_bg }}>
                      {item.children.map(child => (
                        <li key={child.to}>
                          <NavLink
                            to={child.to}
                            onClick={handleNavClick}
                            className={({ isActive }) => isActive ? styles.active : ""}
                            style={({ isActive }) => ({
                              display:"flex", alignItems:"center", gap:8,
                              padding:"8px 14px 8px 36px", borderRadius:6, fontSize:13,
                              textDecoration:"none", transition:"all 0.15s",
                              color: isActive ? T.nav_text_active : T.nav_text,
                              fontWeight: isActive ? 700 : 400,
                              background: isActive ? T.sidebar_active : "transparent",
                            })}>
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* ── Main ── */}
      <main className={`${styles.outletWrapper} ${!sidebarOpen ? styles.sidebarOp : ""}`}
        style={{ background:T.bg, transition:"background 0.25s" }}>
        <div className={`${styles.header} ${sidebarOpen ? styles.flexEnd : styles.spaceBetween}`}
          style={{ background:T.header_bg, borderBottom:`1px solid ${T.header_border}`,
            transition:"background 0.25s, border-color 0.25s" }}>
          <div className={`${styles.menuContainer} ${sidebarOpen ? styles.closeIcon : styles.openIcon}`}
            onClick={toggleSidebar} style={{ color:T.muted, cursor:"pointer" }}>
            <MenuIcon size={18}/>
          </div>

          <div className={styles.headerActions} style={{ display:"flex", alignItems:"center", gap:10 }}>
            {/* ── Theme toggle ── */}
            <ThemeToggle/>

            <Dropdown>
              <MenuButton style={{ background:"none", border:"none", padding:0, cursor:"pointer" }}>
                <div className={styles.userBtn}>
                  <div className={styles.avatar} style={{ background:avatarColor, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>{initials}</div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start" }}>
                    <span style={{ fontSize:13, fontWeight:600, color:T.text, lineHeight:1.2 }}>{displayName}</span>
                    <span style={{ fontSize:10.5, color:T.muted, lineHeight:1.2 }}>{roleLbl}</span>
                  </div>
                  <ChevronDown size={13} style={{ color:T.muted }}/>
                </div>
              </MenuButton>
              <Menu style={{ background:T.card, border:`1px solid ${T.cyan}26`, borderRadius:10, padding:"6px", minWidth:200, boxShadow:T.shadow, zIndex:9999, fontFamily:"Outfit,sans-serif" }}>
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${T.border}`, marginBottom:4 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{displayName}</div>
                  <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{user.email}</div>
                  <div style={{ marginTop:5, fontSize:10.5, padding:"2px 8px", borderRadius:10, background:`${T.cyan}1A`, color:T.cyan, display:"inline-block", fontWeight:600 }}>{roleLbl}</div>
                </div>
                <MenuItem onClick={() => navigate("/employee/profile")}
                  style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 14px", borderRadius:7, cursor:"pointer", color:T.text, fontSize:13 }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${T.cyan}12`}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <User size={14} style={{ color:T.cyan }}/> My Profile
                </MenuItem>
                <MenuItem onClick={() => setShowResetPwd(true)}
                  style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 14px", borderRadius:7, cursor:"pointer", color:T.text, fontSize:13 }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${T.cyan}12`}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <KeyRound size={14} style={{ color:T.amber }}/> Reset Password
                </MenuItem>
                <div style={{ height:1, background:T.border, margin:"4px 0" }}/>
                <MenuItem onClick={logout}
                  style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 14px", borderRadius:7, cursor:"pointer", color:T.red, fontSize:13 }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${T.red}12`}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <LogOut size={14}/> Sign Out
                </MenuItem>
              </Menu>
            </Dropdown>
          </div>
        </div>

        <div className={styles.wrapper} style={{ padding:"10px" }}>
          <Outlet/>
        </div>
      </main>

      {/* ── Reset Password Modal ── */}
      {showResetPwd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => { setShowResetPwd(false); setPwdMsg(""); }}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:420, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Reset Password</h3>
              <button onClick={() => { setShowResetPwd(false); setPwdMsg(""); }} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
              {[
                { key:"current", label:"Current Password",     placeholder:"Your current password" },
                { key:"next",    label:"New Password",          placeholder:"Min 8 characters"      },
                { key:"confirm", label:"Confirm New Password",  placeholder:"Repeat new password"   },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</label>
                  <input type="password" value={pwdForm[key]} placeholder={placeholder}
                    onChange={e => setPwdForm(p => ({...p,[key]:e.target.value}))}
                    style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"10px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
                </div>
              ))}
              {pwdMsg && (
                <div style={{ padding:"9px 14px", borderRadius:8, fontSize:13,
                  background:pwdMsg.startsWith("✅")?`${T.green}14`:`${T.red}14`,
                  color:pwdMsg.startsWith("✅")?T.green:T.red,
                  border:`1px solid ${pwdMsg.startsWith("✅")?`${T.green}33`:`${T.red}33`}`}}>
                  {pwdMsg}
                </div>
              )}
              <button onClick={async () => {
                if (!pwdForm.current || !pwdForm.next) { setPwdMsg("❌ Fill all fields"); return; }
                if (pwdForm.next !== pwdForm.confirm)  { setPwdMsg("❌ Passwords don't match"); return; }
                if (pwdForm.next.length < 8)           { setPwdMsg("❌ Min 8 characters"); return; }
                try {
                  const token = sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token");
                  await (await import("axios")).default.post(
                    `${API}/auth/change-password`,
                    { current_password: pwdForm.current, new_password: pwdForm.next },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setPwdMsg("✅ Password changed successfully");
                  setPwdForm({ current:"", next:"", confirm:"" });
                  setTimeout(() => { setShowResetPwd(false); setPwdMsg(""); }, 2000);
                } catch(e) {
                  setPwdMsg("❌ " + (e.response?.data?.detail || "Error changing password"));
                }
              }} style={{ background:`linear-gradient(135deg,${T.cyan},${T.purple})`, border:"none", borderRadius:9, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, padding:"11px", cursor:"pointer" }}>
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Outer shell — provides ThemeContext ────────────────────────────
const EmployeeLayout = () => (
  <ThemeProvider>
    <EmployeeLayoutInner/>
  </ThemeProvider>
);

export default EmployeeLayout;
